/**
 * useFlipAnimation — wires the pure FLIP utilities into the React commit lifecycle.
 *
 * Orchestrates the FLIP (First → Last → Invert → Play) sequence for tile
 * repositioning after committed board mutations (move/resize/add/remove).
 *
 * TRIGGER (review fix, 2026-07-24): the replay fires when the LAYOUT GEOMETRY
 * SIGNATURE changes — a serialization of every layout entry's tileId:x,y,w,h.
 * The previous trigger compared board.id, which is INVARIANT across all board
 * ops (spread-preserved), so the animation was dead code that every test
 * missed. The signature changes exactly on move/resize/add/remove and stays
 * identical across data refreshes and unrelated re-renders. layout-signature
 * tests below drive REAL board-ops output to keep this from regressing.
 *
 * EXCLUSION (review fix, same date): pointer-drag commits pass the dragged /
 * resized tile's id to captureBeforeCommit. That tile is EXCLUDED from the
 * replay — the ghost/outline was its live feedback, so it snaps into place —
 * while DISPLACED neighbors glide (the whole point of reflow fluidity).
 * Keyboard moves, menu resizes, and removals pass no exclusion: there the
 * committed tile's own motion IS the feedback.
 *
 * HARD RULES:
 * - prefers-reduced-motion → no-op (delegated to playFlipAnimations).
 * - No animation on initial mount or data refresh (signature guard).
 * - Template-swap crossfade classes are not touched — only transform is written.
 * - No new deps — uses existing flip-animate.ts utilities (pure module).
 *
 * Immutability: captured rects are stored in a ref, not state — writes are
 *   transient measurement artefacts, not data mutations.
 */
import { useRef, useLayoutEffect } from 'react';
import {
  captureRects,
  computeInvertedTransforms,
  playFlipAnimations,
} from './flip-animate.js';

/** The slice of a board layout entry the signature depends on. */
export interface LayoutGeometry {
  readonly tileId: string;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/**
 * Serialize layout geometry into a stable signature string. Pure.
 *
 * Two boards have the same signature iff every tile occupies the same cells —
 * exactly the condition under which no reflow animation should play.
 */
export function layoutSignature(layout: readonly LayoutGeometry[]): string {
  return layout
    .map((l) => `${l.tileId}:${l.x},${l.y},${l.w},${l.h}`)
    .join('|');
}

/**
 * Gather the current tile elements from the grid container keyed by data-tile-id.
 * Returns an empty Map if the container is not mounted.
 */
function collectTileElements(gridEl: HTMLElement): Map<string, HTMLElement> {
  const result = new Map<string, HTMLElement>();
  const nodes = gridEl.querySelectorAll<HTMLElement>('[data-tile-id]');
  for (const node of nodes) {
    const id = node.dataset['tileId'];
    if (id) result.set(id, node);
  }
  return result;
}

export interface UseFlipAnimationOptions {
  /**
   * Ref to the grid container element (the div that wraps BoardGrid's output).
   * Elements are queried from here via [data-tile-id].
   */
  readonly gridRef: React.RefObject<HTMLElement | null>;
  /**
   * Layout geometry signature (layoutSignature of the current board layout).
   * The replay fires exactly when this changes between commits.
   */
  readonly layoutSig: string;
}

export interface UseFlipAnimationResult {
  /**
   * Call immediately BEFORE a board commit to snapshot tile positions.
   *
   * @param excludeTileId - for pointer-drag/resize commits: the tile whose
   *   ghost/outline already provided live feedback. It is excluded from the
   *   replay (snaps into place); displaced neighbors still animate. Omit for
   *   keyboard/menu/remove commits, where the tile's own glide is the feedback.
   */
  captureBeforeCommit: (excludeTileId?: string) => void;
}

/** Pending snapshot: pre-commit rects + the tile to exclude from replay. */
interface PendingSnapshot {
  readonly rects: Map<string, DOMRect>;
  readonly excludeTileId?: string;
}

/**
 * Hook that instruments board commits with FLIP position animation.
 *
 * Usage in App.tsx — immediately before each commit:
 *   flip.captureBeforeCommit();            // keyboard / menu / remove
 *   flip.captureBeforeCommit(draggedId);   // pointer drag / resize commits
 *   editable.commitMove(tileId, x, y);
 */
export function useFlipAnimation({
  gridRef,
  layoutSig,
}: UseFlipAnimationOptions): UseFlipAnimationResult {
  // Snapshot captured just before the last board commit; null = none pending.
  const pending = useRef<PendingSnapshot | null>(null);

  // First-mount guard: never animate the initial render.
  const isMountedRef = useRef(false);

  // Previous signature — replay only when the geometry actually changed.
  const prevSigRef = useRef<string>(layoutSig);

  // Runs after every React commit (no dep array); the guards make it a no-op
  // except on true layout mutations. useLayoutEffect fires after DOM mutation
  // and before paint — exactly when inverted transforms must be applied.
  useLayoutEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      prevSigRef.current = layoutSig;
      return;
    }

    // Not a layout mutation (data refresh, drag-state update, etc.) → skip.
    if (layoutSig === prevSigRef.current) return;
    prevSigRef.current = layoutSig;

    const snapshot = pending.current;
    pending.current = null; // consumed either way — never replay twice
    if (!snapshot) return;

    const gridEl = gridRef.current;
    if (!gridEl) return;

    // Exclude the pointer-driven tile: its ghost/outline was the feedback.
    const oldRects = new Map(snapshot.rects);
    if (snapshot.excludeTileId !== undefined) {
      oldRects.delete(snapshot.excludeTileId);
    }

    // "Last" → "Invert" → "Play".
    const elements = collectTileElements(gridEl);
    const newRects = captureRects(elements);
    const inverted = computeInvertedTransforms(oldRects, newRects);
    playFlipAnimations(elements, inverted);
  });

  const captureBeforeCommit = (excludeTileId?: string): void => {
    const gridEl = gridRef.current;
    if (!gridEl) {
      pending.current = null;
      return;
    }
    const elements = collectTileElements(gridEl);
    pending.current = {
      rects: captureRects(elements),
      ...(excludeTileId !== undefined ? { excludeTileId } : {}),
    };
  };

  return { captureBeforeCommit };
}
